/**
 * Bridge the EXISTING deal placeholder sidebar (the CKEditor-era
 * `PlaceholdersSidebarContainer`, whose UI we keep) to the DOCX-NATIVE editor.
 *
 * The sidebar was written to talk to CKEditor via `window` CustomEvents. The
 * docx-native deal editor speaks none of them, so in a docx deal the sidebar's
 * "+" stays disabled, inserts do nothing, and value edits don't reach the
 * document. This hook makes the docx editor a first-class participant in that
 * SAME event protocol — no sidebar UI change.
 *
 * Protocol (sidebar ⇄ editor):
 *  - editor → sidebar: `placeholderCommandStateChange { canInsert }`
 *      Enables/disables the sidebar "+". On an UNrestricted deal it's always
 *      insertable; on a RESTRICTED (locked) deal the "+" is only enabled when
 *      the cursor sits in an editable region — matching what the editor's
 *      restricted-editing lock would actually allow. Emitted on mount, whenever
 *      the sidebar (re)announces `…SidebarReady`, and on every selection change
 *      (via the returned `announceCanInsert`, called from `onSelectionChange`).
 *  - sidebar → editor: `insertPlaceholderAtCursor { uuid, name, required }`
 *      The create-modal minted a `document_placeholder` row; insert a matching
 *      placeholder SDT at the cursor carrying that uuid.
 *  - sidebar → editor: `updatePlaceholderValue { placeholderId, placeholderName, newValue }`
 *      A value was entered / a linked deal-field or bound contact changed; write
 *      it into the placeholder's inner text so the DOCUMENT updates in realtime.
 *      The sidebar persists `current_value` relationally itself; this keeps the
 *      body in sync. Matched by uuid, falling back to name — same as
 *      `scrollToPlaceholder`, and for the same reason: a placeholder that has
 *      been through a docx round trip carries only its alias.
 *  - sidebar → editor: `updatePlaceholderName { placeholderId, previousName, newName, isRequired }`
 *      The sidebar's edit modal renamed the row; rename the control in the body
 *      too. Not just cosmetic: the name is what later value syncs match on, so
 *      a body left on the old alias stops receiving them.
 *  - sidebar → editor: `deletePlaceholderFromSidebar { placeholderId }`
 *      The sidebar deleted the relational row; remove the matching control from
 *      the body so the document and the sidebar agree.
 *  - editor → sidebar: `placeholderDeletedInEditor { placeholderId }`
 *      Emitted by `placeholderSyncPlugin` when a control leaves the document;
 *      the sidebar deletes the row. See that plugin for the undo grace period.
 *  - sidebar → editor: `scrollToPlaceholder { placeholderId, placeholderName }`
 *      "View in doc" — scroll the matching placeholder into view + select it.
 *      Matched by server uuid, falling back to name (migrated placeholders carry
 *      no uuid in the body SDT — only the alias).
 */

import { useCallback, useEffect, useRef } from 'react';
import type { EditorView } from 'prosemirror-view';
import { getDocxNativeEditor } from './docxNativeEditorRegistry';
import { isCursorInsertable } from './plugins/restrictedEditing';

export interface DealPlaceholderBridge {
  /**
   * Recompute + re-announce whether inserting a placeholder is currently
   * allowed (drives the sidebar "+"). Call from the editor's `onSelectionChange`
   * so the affordance tracks the caret on a restricted deal doc.
   */
  announceCanInsert: () => void;
}

export function useDealPlaceholderBridge(
  documentId: string | undefined,
  /** Notify the container so it can autosave after an insert/fill mutates the body. */
  onBodyChanged?: () => void,
  /** Live accessor for the body EditorView (to read the caret's editability). */
  getView?: () => EditorView | null
): DealPlaceholderBridge {
  // Keep the latest getView without re-subscribing the window listeners.
  const getViewRef = useRef(getView);
  getViewRef.current = getView;

  const announceCanInsert = useCallback(() => {
    // Insertable when the doc isn't restricted, or the caret is in an editable
    // region. Without a view yet, default to enabled (matches the prior
    // always-on behaviour); the first selection change refines it.
    const view = getViewRef.current?.() ?? null;
    const canInsert = view ? isCursorInsertable(view) : true;
    window.dispatchEvent(new CustomEvent('placeholderCommandStateChange', { detail: { canInsert } }));
  }, []);

  useEffect(() => {
    if (!documentId) return;
    const handle = () => getDocxNativeEditor(documentId);

    announceCanInsert();
    window.addEventListener('placeholdersSidebarReady', announceCanInsert);

    const onInsert = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const { uuid, name, required } = detail;
      if (!uuid || !name) return;
      handle()?.insertPlaceholderWith?.({ uuid: String(uuid), name: String(name), required: !!required });
      onBodyChanged?.();
    };
    window.addEventListener('insertPlaceholderAtCursor', onInsert);

    const onUpdateValue = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const { placeholderId, placeholderName, newValue } = detail;
      if (!placeholderId && !placeholderName) return;
      // The NAME has to travel: the body carries no uuid for any placeholder
      // that has been through a docx round trip (`sdt.id` is OOXML's numeric
      // `w:id`), so a uuid-only match silently found nothing and the value
      // reached the sidebar card but never the document.
      const filled = handle()?.fillPlaceholderValue?.(
        {
          id: placeholderId != null ? String(placeholderId) : null,
          name: placeholderName != null ? String(placeholderName) : null,
        },
        newValue == null ? '' : String(newValue)
      );
      if (filled) onBodyChanged?.();
    };
    window.addEventListener('updatePlaceholderValue', onUpdateValue);

    const onRename = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const { placeholderId, previousName, newName, isRequired } = detail;
      if (!newName) return;
      if (!placeholderId && !previousName) return;
      const renamed = handle()?.renamePlaceholderMatching?.(
        {
          id: placeholderId != null ? String(placeholderId) : null,
          name: previousName != null ? String(previousName) : null,
        },
        { name: String(newName), required: !!isRequired }
      );
      if (renamed) onBodyChanged?.();
    };
    window.addEventListener('updatePlaceholderName', onRename);

    // "View in doc" — scroll + select the matching placeholder. Match by uuid,
    // fall back to name (migrated placeholders have no uuid in the body).
    const onScrollTo = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const { placeholderId, placeholderName } = detail;
      if (!placeholderId && !placeholderName) return;
      handle()?.focusPlaceholderMatching?.({
        id: placeholderId != null ? String(placeholderId) : null,
        name: placeholderName != null ? String(placeholderName) : null,
      });
    };
    window.addEventListener('scrollToPlaceholder', onScrollTo);

    // The sidebar's delete already removed the relational row; take the control
    // out of the body so the two views agree. Without this the field stayed in
    // the document with nothing behind it.
    const onSidebarDelete = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const { placeholderId, placeholderName } = detail;
      if (!placeholderId && !placeholderName) return;
      const removed = handle()?.removePlaceholderMatching?.({
        id: placeholderId != null ? String(placeholderId) : null,
        name: placeholderName != null ? String(placeholderName) : null,
      });
      if (removed) onBodyChanged?.();
    };
    window.addEventListener('deletePlaceholderFromSidebar', onSidebarDelete);

    return () => {
      window.removeEventListener('deletePlaceholderFromSidebar', onSidebarDelete);
      window.removeEventListener('placeholdersSidebarReady', announceCanInsert);
      window.removeEventListener('insertPlaceholderAtCursor', onInsert);
      window.removeEventListener('updatePlaceholderValue', onUpdateValue);
      window.removeEventListener('updatePlaceholderName', onRename);
      window.removeEventListener('scrollToPlaceholder', onScrollTo);
    };
  }, [documentId, onBodyChanged, announceCanInsert]);

  return { announceCanInsert };
}
